import { useMemo, type KeyboardEvent, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { getReviews, type ReviewAgreement, type ReviewRow } from "../api";
import { useShell } from "../Layout";
import { reasonLabel } from "../reasons";
import { routes } from "../routes";
import { useAsync } from "../useAsync";
import { Badge, Card, CardBody, CardHead, DASH, Empty, Freshness, Kpi, Pane, ScopeBar, When } from "../ui";

/**
 * Every teacher review, and how often the teacher agreed with the agent (owner, 2026-10-14:
 * *"the review where is it stored? if I want to check them in the future"*).
 *
 * 🔴 A REVIEW WAS KEPT AND SHOWN ALMOST NOWHERE. The report shows the review in force; one that
 * a later review replaced, or whose grade was re-graded since, was stored by the append-only
 * tables and read by nothing. Two tabs (the owner's pick of the mockup,
 * https://claude.ai/artifact/SsKJ1rKM15uwTqZ2wVH2gq): *Every review* first, *Agreement* second.
 *
 * ⚠️ THE ADDRESS CARRIES THE TAB, THE FILTER, THE ASSIGNMENT AND THE OPEN REVIEW (`?tab=`,
 * `?show=`, `?asg=`, `?r=`), and the cohort scope (`?scope=all`) as Logs does, so a view is a
 * link.
 * 🔴 THE FIGURES ARE THE SERVER'S, for the scope asked for. The filters below narrow the list
 * only; nothing here counts agreement from a filtered list.
 */
export function ReviewsPage() {
  const { courseId, access } = useShell();
  const [params, setParams] = useSearchParams();
  const mine = params.get("scope") !== "all";
  const tab = params.get("tab") === "agreement" ? "agreement" : "every";
  const q = useAsync(() => getReviews(courseId, mine ? "" : "all"), [courseId, mine]);
  const set = (k: string, v: string) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    setParams(next);
  };
  const show = (t: "every" | "agreement") => {
    const next = new URLSearchParams(params);
    if (t === "agreement") next.set("tab", "agreement"); else next.delete("tab");
    setParams(next);
  };
  const tabs = ["every", "agreement"] as const;
  const onKey = (ev: KeyboardEvent) => {
    if (ev.key !== "ArrowLeft" && ev.key !== "ArrowRight") return;
    ev.preventDefault();
    const n = tabs[(tabs.indexOf(tab) + 1) % tabs.length]!;
    show(n);
    document.getElementById(`rv-tab-${n}`)?.focus();
  };

  return (
    <div className="stack">
      <div className="page-head">
        <p className="eyebrow">Operations</p>
        <h2>Reviews</h2>
        <span className="fresh"><Freshness q={q} /></span>
      </div>
      <Card>
        <ScopeBar name={access.name} on={mine} count={q.data?.rows.length ?? 0}
                  onChange={(on) => set("scope", on ? "" : "all")} />
      </Card>
      <Pane q={q}>
        {(d) => d.rows.length === 0 ? (
          <Card><CardBody>
            <Empty title="No reviews yet"
                   hint={<>Open a grade's report, decide each criterion and press <b>Save
                     review</b>. Every review you save appears here, including ones a later review
                     or a re-grade replaced.{mine && " Other cohorts may have some: remove the filter above."}</>} />
          </CardBody></Card>
        ) : (<>
          <div className="tabs" role="tablist" aria-label="Reviews" onKeyDown={onKey}>
            <button type="button" role="tab" className="tab" id="rv-tab-every"
                    aria-controls="rv-pane-every" aria-selected={tab === "every"}
                    tabIndex={tab === "every" ? 0 : -1} onClick={() => show("every")}>
              Every review<span className="count">{d.rows.length}</span></button>
            <button type="button" role="tab" className="tab" id="rv-tab-agreement"
                    aria-controls="rv-pane-agreement" aria-selected={tab === "agreement"}
                    tabIndex={tab === "agreement" ? 0 : -1} onClick={() => show("agreement")}>
              Agreement</button>
          </div>
          <div id="rv-pane-every" role="tabpanel" aria-labelledby="rv-tab-every" className="rv-pane"
               hidden={tab !== "every"}>
            <EveryReview rows={d.rows} wide={!mine} params={params} set={set} />
          </div>
          <div id="rv-pane-agreement" role="tabpanel" aria-labelledby="rv-tab-agreement"
               hidden={tab !== "agreement"} className="stack rv-pane">
            <Agreement a={d.agreement} wide={!mine} />
          </div>
        </>)}
      </Pane>
    </div>
  );
}

// ── every review ────────────────────────────────────────────────────────────────────────────

const FILTERS: { key: string; label: string; hint: string; test: (r: ReviewRow) => boolean }[] = [
  { key: "", label: "all", hint: "Every review, newest first.", test: () => true },
  { key: "calibration", label: "calibration", hint: "Grades you marked as cases you are sure of.",
    test: (r) => r.calibration },
  { key: "moved", label: "moved a mark", hint: "You adjusted at least one criterion.",
    test: (r) => r.moved },
  { key: "agreed", label: "agreed throughout", hint: "You kept the agent's mark on every criterion.",
    test: (r) => !r.moved },
  { key: "old", label: "replaced or re-graded",
    hint: "A later review took over, or the grade it reviewed has changed since.",
    test: (r) => r.state !== "in_force" },
];

const STATE: Record<ReviewRow["state"], { word: string; tone: "ok" | "held" }> = {
  in_force: { word: "in force", tone: "ok" },
  replaced: { word: "replaced", tone: "held" },
  regraded: { word: "re-graded since", tone: "held" },
  archived: { word: "grade archived", tone: "held" },
};

const asgKey = (r: { course_id: string; course_work_id: string }) =>
  `${r.course_id}:${r.course_work_id}`;
const shortDay = (iso: string | null) => iso
  ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" }) : DASH;
const pts = (n: number | null | undefined) => (n == null ? DASH : String(Math.round(n * 100) / 100));

function EveryReview({ rows, wide, params, set }: {
  rows: ReviewRow[]; wide: boolean; params: URLSearchParams; set: (k: string, v: string) => void;
}) {
  const showKey = params.get("show") ?? "";
  const filter = FILTERS.find((f) => f.key === showKey) ?? FILTERS[0]!;
  const asg = params.get("asg") ?? "";
  const assignments = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of rows) if (!m.has(asgKey(r))) m.set(asgKey(r), r.assignment_title || r.course_work_id);
    return [...m];
  }, [rows]);
  // 🔴 A COUNT IS THE COUNT OF WHAT IT SHOWS: each chip's number is its own rows, under the
  // assignment chosen, from the one predicate the list uses.
  const inAsg = rows.filter((r) => !asg || asgKey(r) === asg);
  const shown = inAsg.filter(filter.test);
  const chosen = params.get("r") ?? "";
  // ⚠️ ONLY A CHOSEN REVIEW OPENS OVER THE LIST on a narrow screen; where there is room the first
  // in the list is shown beside it — the Applicants rule.
  const current = shown.find((r) => r.id === chosen) ?? shown[0];
  const position = current ? shown.indexOf(current) : -1;
  return (
    <div className="ap-wrap">
      <div className="log-filters ap-filters" role="group" aria-label="Show">
        {FILTERS.map((f) => (
          <button key={f.key || "all"} type="button" title={f.hint}
                  className={filter.key === f.key ? "chip on" : "chip"}
                  aria-pressed={filter.key === f.key} onClick={() => set("show", f.key)}>
            {f.label}<span className="cnt">{inAsg.filter(f.test).length}</span>
          </button>))}
        {assignments.length > 1 && (
          <select className="select rv-asg" aria-label="Assignment" value={asg}
                  onChange={(e) => set("asg", e.target.value)}>
            <option value="">Every assignment</option>
            {assignments.map(([k, t]) => <option key={k} value={k}>{t}</option>)}
          </select>)}
      </div>
      <div className={chosen && current?.id === chosen ? "ap-split ap-open" : "ap-split"}>
        <div className="card ap-list-card rv-list">
          {shown.length === 0
            ? <p className="ap-empty">Nothing matches that.</p>
            : (
              <ol className="ap-list" aria-label="Reviews">
                {shown.map((r) => (
                  <li key={r.id}>
                    <button type="button"
                            className={`ap-row${r.id === current?.id ? " on" : ""}${
                              r.state === "in_force" ? "" : " faint"}`}
                            aria-current={r.id === current?.id ? "true" : undefined}
                            onClick={() => set("r", r.id)}>
                      <span className="ap-who">
                        <b>{r.student || "unnamed"}</b>
                        <span className="ap-mail">
                          {r.assignment_title || r.course_work_id}{wide && ` · ${r.cohort}`}</span>
                      </span>
                      <span className="ap-meta">
                        {r.calibration && <Badge tone="plain">calibration</Badge>}
                        {r.state !== "in_force" && (
                          <Badge tone={STATE[r.state].tone}>{STATE[r.state].word}</Badge>)}
                        <Marks agent={r.agent_points} you={r.points} />
                        <span className="ap-date">{shortDay(r.created_at)}</span>
                      </span>
                    </button>
                  </li>))}
              </ol>)}
        </div>
        {current && (
          <ReviewPanel key={current.id} row={current} wide={wide} position={position}
                       total={shown.length}
                       onGo={(by) => { const next = shown[position + by]; if (next) set("r", next.id); }}
                       onClose={() => set("r", "")} />)}
      </div>
    </div>
  );
}

/** The agent's mark and the teacher's, the teacher's bold. One number when they agree. */
function Marks({ agent, you, max }: { agent: number | null; you: number; max?: number }) {
  return (
    <span className="rv-marks num">
      {agent !== you && <>{pts(agent)}<span className="rv-arrow" aria-hidden="true">→</span>
        <span className="sr"> to </span></>}
      <b>{pts(you)}</b>{max != null && <span className="muted">/{pts(max)}</span>}
    </span>);
}

/** One review, opened on the right. */
function ReviewPanel({ row, wide, position, total, onGo, onClose }: {
  row: ReviewRow; wide: boolean; position: number; total: number;
  onGo: (by: number) => void; onClose: () => void;
}) {
  const said = row.state === "replaced" && row.replaced_by
    ? <>Replaced by {row.replaced_by.reviewer}'s review of <When iso={row.replaced_by.created_at} />.
        It is kept, and it no longer counts.</>
    : row.state === "regraded"
      ? <>The grade was re-graded{row.now?.graded_at && <> on <When iso={row.now.graded_at} /></>}
          {row.now?.points != null && <> and now reads {pts(row.now.points)}</>}
          {row.now?.reviewed ? ", which has a review of its own" : ", which nobody has reviewed"}.
          This review is of the grade before, and still counts: it measured that run.</>
      : row.state === "archived"
        ? <>A setup change archived the grade this review is of, and a re-grade is queued. The
            review still counts: it measured that run.</>
        : null;
  return (
    <aside className="card ap-panel" aria-label={`Review of ${row.student || "an unnamed student"}`}>
      <div className="ap-head">
        <button type="button" className="btn ghost sm ap-x" aria-label="Close the review"
                onClick={onClose}>×</button>
        <div className="ap-title">
          <h3>{row.student || "unnamed"}</h3>
          <span className="ap-mail">{row.assignment_title || row.course_work_id}</span>
        </div>
        <span className="ap-nav">
          <button type="button" className="btn sm icon" aria-label="Previous review"
                  disabled={position <= 0} onClick={() => onGo(-1)}>‹</button>
          <span className="ap-pos">{position >= 0 ? `${position + 1} of ${total}` : DASH}</span>
          <button type="button" className="btn sm icon" aria-label="Next review"
                  disabled={position < 0 || position >= total - 1} onClick={() => onGo(1)}>›</button>
        </span>
      </div>
      <div className="ap-badges">
        <Badge tone={STATE[row.state].tone}>{STATE[row.state].word}</Badge>
        {row.calibration && <Badge tone="plain">calibration case</Badge>}
        {wide && <span className="tierchip">{row.cohort}</span>}
        <span className="spacer" />
        <span className="small muted">by {row.reviewer} · <When iso={row.created_at} /></span>
      </div>
      {/* ⚠️ ONE SPAN: `.alert` LAYS ITS CHILDREN OUT IN A ROW, so a sentence with a date in it
          came out as three columns — [X] seen in a render. */}
      {said && (
        <div className="rv-pad">
          <p className="alert small rv-state" role="status"><span>{said}</span></p></div>)}
      <div className="rv-total">
        <div><span className="rv-total-k">Agent</span>
          <span className="rv-total-v num">{pts(row.agent_points)}</span></div>
        <span className="rv-arrow big" aria-hidden="true">→</span>
        <div><span className="rv-total-k">{row.state === "in_force" ? "Your review" : "This review"}</span>
          <span className="rv-total-v num you">{pts(row.points)}
            <span className="muted">/{pts(row.max_points)}</span></span></div>
        <span className="spacer" />
        <Link className="btn sm" to={routes.submission(row.course_id, row.submission_id)}>
          Open the grade's report</Link>
      </div>
      <div className="ap-scroll">
        <section className="ap-sec">
          <h4>Criteria</h4>
          <ol className="rv-crits">
            {row.criteria.map((c) => (
              <li key={c.id} className="rv-crit">
                <span className="rv-crit-h">
                  <b>{c.id}</b>
                  <span className="rv-crit-l">{c.label}</span>
                  <span className="spacer" />
                  {c.verdict === "agree"
                    ? <Badge tone="ok">agree</Badge> : <Badge tone="warn">adjusted</Badge>}
                  <Marks agent={c.agent_points} you={c.points} max={c.weight} />
                </span>
                {c.reason && <span className="rv-reason">{reasonLabel(c.reason)}</span>}
                {c.note && <p className="rv-crit-note">{c.note}</p>}
              </li>))}
          </ol>
        </section>
        {row.note && (
          <section className="ap-sec"><h4>Review note</h4><p className="rv-note">{row.note}</p></section>)}
        <section className="ap-sec">
          <h4>The grade it reviewed</h4>
          <dl className="rv-facts">
            <dt>Graded</dt><dd><When iso={row.grade.graded_at} /></dd>
            <dt>Grader</dt>
            <dd>{row.grade.model || DASH}{row.grade.effort && ` · ${row.grade.effort} effort`}</dd>
            <dt>Commit</dt><dd className="mono">{row.grade.commit_sha.slice(0, 7) || DASH}</dd>
            <dt>Rubric</dt><dd className="mono">{row.grade.rubric_sha256.slice(0, 12) || DASH}</dd>
          </dl>
        </section>
        <p className="small muted">A review is never edited. To change it, open the report and
          review the grade again; this one stays here, replaced.</p>
      </div>
    </aside>
  );
}

// ── agreement ───────────────────────────────────────────────────────────────────────────────

const signed = (n: number | null | undefined) => {
  if (n == null) return DASH;
  const r = Math.round(n * 10) / 10;
  return `${r > 0 ? "+" : r < 0 ? "−" : ""}${Math.abs(r)}`;
};
const pct = (a: number, b: number) => (b ? `${Math.round((100 * a) / b)}%` : DASH);

/** A figure from fewer reviews than the server's threshold says so, beside it. */
function Few({ n, few }: { n: number; few: number }) {
  return n < few
    ? <span className="rv-few" title={`Fewer than ${few} reviews: too few to tell much yet`}>few</span>
    : null;
}

function Agreement({ a, wide }: { a: ReviewAgreement; wide: boolean }) {
  return (<>
    <div className="counts">
      <Kpi label="Grades reviewed" value={a.grades} hint="each counted once, by its last review" />
      <Kpi label="Criteria agreed"
           value={<>{a.criteria_agreed} <span className="rv-of">of {a.criteria}</span></>}
           hint={`${pct(a.criteria_agreed, a.criteria)} — you kept the agent's mark`} />
      <Kpi label="Average change" value={<>{signed(a.mean_change)} <span className="rv-of">pts</span></>}
           hint="your total minus the agent's; above 0, you mark higher" />
      <Kpi label="Calibration cases" value={a.calibration} hint="grades you are sure of" />
    </div>
    <Card>
      <CardHead title="By criterion"
                desc="Where you moved the agent's mark, and which way. Each grade counts once, by its last review; advisory criteria are never reviewed." />
      <CardBody tight>
        <div className="tablewrap">
          <table className="tb rv-tb">
            <thead><tr><th>Criterion</th><th className="r">Reviewed</th><th className="r">Agreed</th>
              <th className="r">Avg change</th><th>Main reason</th></tr></thead>
            <tbody>
              {a.by_criterion.map((c) => (
                <tr key={`${c.id}:${c.label}`}>
                  <td><b>{c.id}</b> {c.label}</td>
                  <td className="r num">{c.reviewed} <Few n={c.reviewed} few={a.few} /></td>
                  <td className="r num">{c.agreed} <span className="muted">({pct(c.agreed, c.reviewed)})</span></td>
                  <td className="r num">{signed(c.mean_change)}</td>
                  <td>{c.main_reason
                    ? <>{reasonLabel(c.main_reason.reason)} <span className="muted">×{c.main_reason.n}</span></>
                    : <span className="muted">{DASH}</span>}</td>
                </tr>))}
            </tbody>
          </table>
        </div>
      </CardBody>
    </Card>
    <div className="rv-two">
      <Card>
        <CardHead title="By assignment" />
        <CardBody tight>
          <Groups few={a.few} rows={a.by_assignment.map((g) => ({
            key: `${g.course_id}:${g.course_work_id}`,
            name: <>{g.title || g.course_work_id}{wide && <span className="muted"> · {g.cohort}</span>}</>,
            ...g }))} head="Assignment" />
        </CardBody>
      </Card>
      <Card>
        <CardHead title="By grader setting" desc="The model and effort the reviewed grade was made with." />
        <CardBody tight>
          <Groups few={a.few} head="Model · effort" rows={a.by_setting.map((g) => ({
            key: `${g.model}:${g.effort}`,
            name: <>{g.model || DASH}{g.effort && ` · ${g.effort}`}</>, ...g }))} />
        </CardBody>
      </Card>
    </div>
    <Card>
      <CardHead title="Why marks moved" desc="The reason you gave each time you adjusted a criterion." />
      <CardBody>
        {a.reasons.length === 0
          ? <p className="muted small">No mark has been moved yet.</p>
          : (<>
            <ol className="rv-bars">
              {a.reasons.map((r) => (
                <li key={r.reason}>
                  <span className="rv-bar-k">{reasonLabel(r.reason)}</span>
                  <span className="rv-bar" aria-hidden="true">
                    <i style={{ width: `${Math.round((100 * r.n) / a.reasons[0]!.n)}%` }} /></span>
                  <span className="rv-bar-n num">{r.n}</span>
                </li>))}
            </ol>
            <p className="small muted rv-foot">
              {a.reasons.reduce((n, r) => n + r.n, 0)} criteria moved across {a.moved_grades}{" "}
              grade{a.moved_grades === 1 ? "" : "s"}.</p>
          </>)}
      </CardBody>
    </Card>
  </>);
}

function Groups({ rows, head, few }: {
  rows: { key: string; name: ReactNode; reviewed: number; all_agreed: number;
          mean_change: number | null }[];
  head: string; few: number;
}) {
  return (
    <div className="tablewrap">
      <table className="tb rv-tb">
        <thead><tr><th>{head}</th><th className="r">Reviewed</th><th className="r">All agreed</th>
          <th className="r">Avg change</th></tr></thead>
        <tbody>
          {rows.map((g) => (
            <tr key={g.key}>
              <td>{g.name}</td>
              <td className="r num">{g.reviewed} <Few n={g.reviewed} few={few} /></td>
              <td className="r num">{g.all_agreed} <span className="muted">({pct(g.all_agreed, g.reviewed)})</span></td>
              <td className="r num">{signed(g.mean_change)}</td>
            </tr>))}
        </tbody>
      </table>
    </div>
  );
}
